import React, { useContext, useState } from "react";
import "./PFHModal.css";
import {
  sendOtpAPI,
  sendOtpAPIPFH,
  verifyOtpAPI,
  verifyOtpPFHAPI,
} from "../../../../Utils/CheckoutAPIs/CheckoutAPIs";
import { UserContext } from "../../CheckoutPage";
import ReactGA from "react-ga4";

interface PFHModalProps {
  isOpen: boolean;
  onClose: () => void;
  onVerificationSuccess: (mobileNumber: string, pfhVerified: boolean) => void;
  isPFH?: boolean;
  onVerificationFailed: (mobileNumber: string) => void;
}

const PFHModal: React.FC<PFHModalProps> = ({
  isOpen,
  onClose,
  onVerificationSuccess,
  isPFH = false,
  onVerificationFailed,
}) => {
  const [mobileNumber, setMobileNumber] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [otp, setOtp] = useState("");
  const [error, setError] = useState("");
  const [isOtpSent, setIsOtpSent] = useState(false);
  const [resendTimer, setResendTimer] = useState(30);
  const [canResend, setCanResend] = useState(false);
  const [isOTPVerified, setIsOTPVerified] = useState(false);
  const [showReset, setShowReset] = useState(false);
  const userContext = useContext(UserContext);
  const setUserDetails = userContext?.setUserDetails ?? (() => {});
  const userDetails = userContext?.userDetails ?? {
    name: "",
    email: "",
    school: "",
    schoolID: 0,
    grade: 0,
    mobileNumber: "",
    address: "",
    flatNo: "",
    city: "",
    state: "",
    pincode: "",
  };

  if (!isOpen) return null;

  const startResendTimer = () => {
    setCanResend(false);
    setResendTimer(30);
    const timer = setInterval(() => {
      setResendTimer((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          setCanResend(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const mobileValidation = /^[1-9][0-9]{9}$/;

  const handleSendOTP = async () => {
    // Track OTP send button click
    ReactGA.event({
      category: "PFH_Verification",
      action: "5678_pfh_send_otp_button",
      label: "Discount_Flow",
    });

    setIsLoading(true);
    setError("");

    try {
      const response = await sendOtpAPIPFH(mobileNumber);
      if (response.status === "Success") {
        setIsOtpSent(true);
        startResendTimer();
      } else {
        setShowReset(true);
        setError(
          "This number isn't eligible for a discount. Try a different mobile number or proceed with standard pricing."
        );
      }
    } catch (err) {
      setShowReset(true);
      setError(
        "This number isn't eligible for a discount. Try a different mobile number or proceed with standard pricing."
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyOTP = async () => {
    // Track verify OTP button click
    ReactGA.event({
      category: "PFH_Verification",
      action: "5678_pfh_verify_otp_button",
      label: "Discount_Flow",
    });

    if (otp.length !== 4) {
      setError("Please enter a valid 4-digit OTP");
      return;
    }

    setIsLoading(true);
    setError("");

    try {
      const response = await (isPFH ? verifyOtpPFHAPI : verifyOtpAPI)(
        mobileNumber,
        otp
      );

      if (response.status === "Success") {
        setIsOTPVerified(true);

        if (
          isPFH &&
          typeof response.data === "object" &&
          "pfh" in response.data
        ) {
          if (response.data.pfh) {
            sessionStorage.setItem("mobileNumber", mobileNumber);
            onVerificationSuccess(mobileNumber, true);
            onClose();
          } else {
            onVerificationSuccess(mobileNumber, false);
          }
        } else {
          setError("Invalid Attempt. Please try again.");
        }
      } else {
        setError("Invalid OTP. Please try again.");
      }
    } catch (err) {
      console.error("Error verifying OTP:", err);
      setError("Failed to verify OTP. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const resetForm = () => {
    // Track try different number button
    ReactGA.event({
      category: "PFH_Verification",
      action: "5678_pfh_try_different_number",
      label: "Discount_Flow",
    });

    setMobileNumber("");
    setOtp("");
    setError("");
    setIsOtpSent(false);
    setCanResend(false);
    setResendTimer(30);
    setIsOTPVerified(false);
    setShowReset(false);
    sessionStorage.removeItem("mobileNumber");
  };

  const proceedWithStandardPricing = () => {
    // Track standard pricing button
    ReactGA.event({
      category: "PFH_Verification",
      action: "5678_pfh_standard_pricing_button",
      label: "Discount_Flow",
    });

    onVerificationFailed(mobileNumber);
    onClose();
  };

  return (
    <div className="pfh-modal-overlay">
      <div className="pfh-modal-content">
        <div className="pfh-modal-header">
          <div className="pfh-modal-header-text">
            Enter the mobile number used to purchase the Personal Finance
            Handbook to avail your discount.
          </div>
        </div>
        <div className="pfh-modal-body">
          <div className="phone-input-container">
            <input
              type="text"
              className="phone-input"
              value={mobileNumber}
              onChange={(e) => {
                const value = e.target.value.replace(/\D/g, "").slice(0, 10);
                const isValidMobile = mobileValidation.test(value);

                // Only track when a valid phone number is completed
                if (isValidMobile && value.length === 10) {
                  ReactGA.event({
                    category: "PFH_Verification",
                    action: "5678_pfh_phone_filled",
                    label: "Discount_Flow",
                  });
                }

                setError(
                  isValidMobile ? "" : "Please enter a valid mobile number."
                );
                setMobileNumber(value);
                setUserDetails((prev) => ({ ...prev, mobileNumber: value }));
                sessionStorage.setItem("mobileNumber", value);
              }}
              placeholder="Enter mobile number"
              maxLength={10}
              disabled={isOtpSent && !showReset}
            />
            {isOtpSent && !showReset && (
              <div className="phone-verified-badge">✓</div>
            )}
          </div>

          {showReset && (
            <>
              <button
                className="pfh-modal-button reset-button"
                onClick={resetForm}
              >
                Try with different number
              </button>
              <button
                className="pfh-modal-button proceed-standard-button"
                onClick={proceedWithStandardPricing}
              >
                Proceed with standard pricing
              </button>
            </>
          )}

          {!isOtpSent && !showReset && (
            <button
              className="pfh-modal-button"
              onClick={handleSendOTP}
              disabled={isLoading || mobileNumber.length !== 10}
            >
              {!isLoading && "Send OTP"}
              {isLoading && "Sending..."}
            </button>
          )}

          {isOtpSent && (
            <div className="otp-section">
              <input
                type="text"
                maxLength={4}
                value={otp}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, "").slice(0, 4);
                  setOtp(value);

                  // Only track when full OTP is entered
                  if (value.length === 4) {
                    ReactGA.event({
                      category: "PFH_Verification",
                      action: "5678_pfh_otp_filled",
                      label: "Discount_Flow",
                    });
                  }
                }}
                placeholder="Enter 4-digit OTP"
                className="otp-input"
              />
              <div className="otp-actions">
                <button
                  className="pfh-modal-button verify-button"
                  onClick={handleVerifyOTP}
                  disabled={isLoading || otp.length !== 4 || isOTPVerified}
                >
                  {!isLoading && "Verify OTP"}
                  {isLoading && "Verifying..."}
                </button>
                <button
                  className="pfh-modal-button resend-button"
                  onClick={handleSendOTP}
                  disabled={!canResend || isOTPVerified}
                >
                  {canResend && "Resend OTP"}
                  {!canResend && `Resend in ${resendTimer}s`}
                </button>
              </div>
            </div>
          )}

          {error && <div className="error-message">{error}</div>}
        </div>
      </div>
    </div>
  );
};

export default PFHModal;
